/**
 * CustomerTryOnStudio — Premium Mobile-First TryOn Experience
 *
 * This is the orchestrator. All state is managed by custom hooks in ./CustomerTryOnStudio/hooks/
 * and all UI is rendered by components in ./CustomerTryOnStudio/components/ and screens/.
 *
 * URL format: /<partner_id>/session/<session_id>
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { X, Loader2, Filter, Lightbulb } from 'lucide-react';
import toast from 'react-hot-toast';

import CameraCapture from '../../components/CameraCapture';
import SizeRecommendPanel from '../../components/SizeRecommendPanel';
import TryOnColorChangePicker from '../../components/TryOnColorChangePicker';

// Hooks
import { useSession } from './CustomerTryOnStudio/hooks/useSession';
import { useGarments } from './CustomerTryOnStudio/hooks/useGarments';
import { useCart } from './CustomerTryOnStudio/hooks/useCart';
import { useTryOn } from './CustomerTryOnStudio/hooks/useTryOn';

// Components
import { StudioHeader } from './CustomerTryOnStudio/components/StudioHeader';
import { Stage } from './CustomerTryOnStudio/components/Stage';
import { HistoryStrip } from './CustomerTryOnStudio/components/HistoryStrip';
import { ArcCarousel } from './CustomerTryOnStudio/components/ArcCarousel';
import { CartDrawer } from './CustomerTryOnStudio/components/CartDrawer';
import { ProductDetailModal } from './CustomerTryOnStudio/components/ProductDetailModal';
import { FiltersModal } from './CustomerTryOnStudio/components/FiltersModal';
import { ProTipsModal } from './CustomerTryOnStudio/components/ProTipsModal';
import { SessionLimitBanner } from './CustomerTryOnStudio/components/SessionLimitBanner';
import { TryonLimitModal } from './CustomerTryOnStudio/components/TryonLimitModal';
import { FullscreenGallery } from './CustomerTryOnStudio/components/FullscreenGallery';
import { StageOverlay } from './CustomerTryOnStudio/components/StageOverlay';
import { SessionEndedScreen } from './CustomerTryOnStudio/screens/SessionEndedScreen';

// Constants
import { API_URL } from './CustomerTryOnStudio/constants';

const CustomerTryOnStudio = () => {
  const { partnerId, sessionId } = useParams();
  const navigate = useNavigate();

  // ─── Pure UI state (not business logic) ────────────────────────────────────
  const [cartOpen, setCartOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [proTipsOpen, setProTipsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewport, setViewport] = useState('mobile');

  // AI Color Change — when user picks a swatch we keep the recolored URL in
  // local state and prefer it over the underlying try-on stage asset until
  // the user resets or a new try-on overrides it.
  const [colorChangeOverride, setColorChangeOverride] = useState(null);

  // Viewport detection
  useEffect(() => {
    const checkViewport = () => {
      const w = window.innerWidth;
      setViewport(w >= 1024 ? 'desktop' : w >= 768 ? 'tablet' : 'mobile');
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // ─── Hooks ─────────────────────────────────────────────────────────────────
  const {
    session, setSession, loading, error,
    sessionEnded, sessionEndReason, sessionCountdown,
    emailSent, sendingEmail, handleEndSession,
  } = useSession(partnerId, sessionId);

  const {
    displayedGarments, selectedGarmentIndex, setSelectedGarmentIndex,
    filters, setFilters, categories, hasMoreItems, loadingMore, loadMoreGarments,
  } = useGarments(session);

  const {
    softCart, setSoftCart, hardCart, setHardCart,
    isInCart, handleAddToCart, handleRemoveFromCart, handleCheckout,
  } = useCart(session);

  const {
    stageAsset, history, selectedHistoryIndex,
    tryOnLoading, tryOnCache, lastTryOnAsset,
    humanImage, customerPhotoS3Key, photoUploading,
    generatedMoments, momentsLoading, momentsPopupOpen, setMomentsPopupOpen,
    sizeHistory, setSizeHistory,
    tryonLimitReached, setTryonLimitReached,
    productDetailModal, setProductDetailModal,
    fullscreenGallery, setFullscreenGallery,
    fileInputRef,
    handleImageSelect, handleCameraCapture, handleTryOn,
    handleGenerateMoment, handleHistorySelect, openGallery, resetPhoto,
  } = useTryOn({ session, setSession, displayedGarments, selectedGarmentIndex, setSoftCart });

  // ─── Derived state ──────────────────────────────────────────────────────────
  const selectedGarment = displayedGarments[selectedGarmentIndex];
  const canTryOn = !!humanImage && !!selectedGarment && !tryOnLoading;

  // Reset the color-change override whenever the underlying try-on changes,
  // so we don't keep a Burgundy overlay glued onto a different garment.
  useEffect(() => {
    setColorChangeOverride(null);
  }, [lastTryOnAsset?.id]);

  // The product associated with the visible try-on. We use this product's
  // color_variants and color_change_enabled flag to decide whether to render
  // the swatch picker on the stage.
  const tryonProduct = lastTryOnAsset?.garment || null;
  const showColorChangePicker = !!(
    lastTryOnAsset?.s3_key &&
    tryonProduct?.color_change_enabled &&
    (tryonProduct?.color_variants || []).filter(v => v && v.hex_code).length >= 2
  );

  // Wraps handleEndSession with the current history + sizeHistory (for emailing results)
  const handleEndSessionWithHistory = useCallback(
    (reason) => handleEndSession(reason, history, sizeHistory),
    [handleEndSession, history, sizeHistory]
  );

  // Add to cart from the session-ended summary screen (needs setHardCart for local update)
  const handleAddToCartFromSummary = useCallback(
    async (item) => {
      const garment = item.garment;
      if (!garment) { toast.error('Product info not available'); return; }
      const productId = garment.product_id || garment.id;
      if (hardCart.some((p) => (p.product_id || p.id) === productId)) {
        toast.error('Already in cart');
        return;
      }
      setHardCart((prev) => [...prev, garment]);
      if (session?.session_token) {
        try {
          await fetch(
            `${API_URL}/api/public-studio/session/hard-cart/add?session_token=${session.session_token}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ product_id: productId }),
            }
          );
        } catch (e) { console.error('Failed to sync hard cart:', e); }
      }
      toast.success(`${garment.product_name || 'Item'} added to cart!`);
    },
    [session, hardCart, setHardCart]
  );

  // ─── Early returns ──────────────────────────────────────────────────────────
  if (sessionEnded) {
    return (
      <SessionEndedScreen
        session={session}
        history={history}
        hardCart={hardCart}
        sizeHistory={sizeHistory}
        sendingEmail={sendingEmail}
        emailSent={emailSent}
        sessionEndReason={sessionEndReason}
        onAddToCart={handleAddToCartFromSummary}
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-slate-700 mb-6">{error}</p>
          <button
            onClick={() => navigate(`/${partnerId}/customers/tryon`)}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium"
          >
            Start Over
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // ─── Main render ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col bg-white" style={{ minHeight: '100dvh' }}>
      {/* Hidden file input for photo upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageSelect}
        className="hidden"
      />

      {/* Sticky header */}
      <StudioHeader
        session={session}
        history={history}
        hardCartLength={hardCart.length}
        onGalleryOpen={openGallery}
        onCartOpen={() => setCartOpen(true)}
      />

      {/* Main content */}
      <main className="flex-1 flex flex-col lg:flex-row bg-white">
        {/* Desktop/tablet vertical history strip */}
        {viewport !== 'mobile' && history.length > 0 && (
          <aside className="hidden md:block w-20 lg:w-24 border-r border-slate-100 p-2">
            <HistoryStrip
              items={history}
              onSelect={handleHistorySelect}
              selectedIndex={selectedHistoryIndex}
              isVertical
            />
          </aside>
        )}

        {/* Center column */}
        <div className="flex-1 flex flex-col px-3 sm:px-4 py-2 sm:pb-4 max-w-2xl mx-auto w-full">
          {/* Stage + overlays */}
          <div
            className="relative cursor-pointer"
            onClick={() => {
              if (momentsPopupOpen) { setMomentsPopupOpen(false); return; }
              if (humanImage && history.length > 0) openGallery();
            }}
          >
            <Stage
              image={colorChangeOverride?.image_url || stageAsset}
              isLoading={tryOnLoading}
              loadingMessage="Creating your look..."
              onUploadClick={() => fileInputRef.current?.click()}
              onCameraClick={() => setCameraOpen(true)}
              hasPhoto={!!humanImage}
              bottomOverlay={showColorChangePicker ? (
                <TryOnColorChangePicker
                  tryonKey={lastTryOnAsset.s3_key}
                  product={{
                    sku_id: tryonProduct.sku_id,
                    color_variants: tryonProduct.color_variants,
                    color_change_enabled: tryonProduct.color_change_enabled,
                    color_change_quality: tryonProduct.color_change_quality,
                    dress_type: tryonProduct.dress_type,
                  }}
                  currentImageUrl={colorChangeOverride?.image_url}
                  onResult={(result) => {
                    if (result.reset || !result.image_url) {
                      setColorChangeOverride(null);
                    } else {
                      setColorChangeOverride({
                        image_url: result.image_url,
                        hex: result.hex,
                        name: result.name,
                      });
                    }
                  }}
                />
              ) : null}
            />

            {/* Change-photo X button */}
            {humanImage && !tryOnLoading && (
              <button
                onClick={(e) => { e.stopPropagation(); resetPhoto(); }}
                className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur rounded-full shadow-sm hover:bg-white transition-colors z-10"
              >
                <X className="w-4 h-4 text-slate-600" />
              </button>
            )}

            {/* Stage action overlay (Moments + Try-On/Add-to-Cart) */}
            {humanImage && !tryOnLoading && selectedGarment && (
              <StageOverlay
                selectedGarment={selectedGarment}
                tryOnCache={tryOnCache}
                isInCart={isInCart}
                canTryOn={canTryOn}
                session={session}
                momentsPopupOpen={momentsPopupOpen}
                setMomentsPopupOpen={setMomentsPopupOpen}
                momentsLoading={momentsLoading}
                generatedMoments={generatedMoments}
                onTryOn={handleTryOn}
                onAddToCart={handleAddToCart}
                onGenerateMoment={handleGenerateMoment}
              />
            )}
          </div>

          {/* Mobile history strip */}
          {viewport === 'mobile' && history.length > 0 && (
            <div className="mt-2 -mx-4 bg-slate-50">
              <HistoryStrip
                items={history}
                onSelect={handleHistorySelect}
                selectedIndex={selectedHistoryIndex}
              />
            </div>
          )}

          {/* Session limit banner (shows when try-on quota is exhausted) */}
          <AnimatePresence>
            {(session?.tryons_used || 0) >= (session?.max_tryons || 5) && (
              <SessionLimitBanner
                countdown={sessionCountdown}
                onLearnMore={() => setTryonLimitReached(true)}
              />
            )}
          </AnimatePresence>

          {/* Size Recommendation Panel — visible as soon as photo is uploaded */}
          {humanImage && selectedGarment?.size_chart && Object.keys(selectedGarment.size_chart || {}).length > 0 && (
            <div className="mt-3">
              {photoUploading && !customerPhotoS3Key ? (
                <div style={{
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                  padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 12, color: '#64748b',
                }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }}>
                    <circle cx="7" cy="7" r="5.5" fill="none" stroke="#cbd5e1" strokeWidth="2" />
                    <path d="M7 1.5a5.5 5.5 0 0 1 5.5 5.5" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  Preparing photo for size analysis&hellip;
                </div>
              ) : customerPhotoS3Key ? (
                <SizeRecommendPanel
                  key={selectedGarment?.product_id || selectedGarment?.id}
                  apiFetcher={async (heightCm) => {
                    const pid = selectedGarment?.product_id || selectedGarment?.id;
                    const resp = await fetch(`${API_URL}/api/public-studio/session/size-recommend`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'X-Session-Token': session.session_token },
                      body: JSON.stringify({ customer_s3_key: customerPhotoS3Key, height_cm: heightCm, product_id: pid }),
                    });
                    const data = await resp.json();
                    if (!resp.ok) throw new Error(typeof data.detail === 'object' ? (data.detail.message || 'Failed') : (data.detail || 'Failed'));
                    return data;
                  }}
                  productId={selectedGarment?.product_id || selectedGarment?.id}
                  garment={selectedGarment}
                  onResult={(pid, res, g) => setSizeHistory((prev) => ({ ...prev, [pid]: { result: res, garment: g } }))}
                />
              ) : null}
            </div>
          )}

          {/* Arc carousel — swipeable garment selector */}
          <div className="mt-2" onClick={() => momentsPopupOpen && setMomentsPopupOpen(false)}>
            <ArcCarousel
              garments={displayedGarments}
              selectedIndex={selectedGarmentIndex}
              onSelect={setSelectedGarmentIndex}
              onCenterClick={(garment) => setProductDetailModal(garment)}
              tryOnCache={tryOnCache}
              onLoadMore={loadMoreGarments}
              hasMore={hasMoreItems}
              loadingMore={loadingMore}
            />
          </div>

          {/* Filter & Pro Tips row */}
          <div className="mt-4 flex items-center justify-between px-2">
            <button
              onClick={() => setFiltersOpen(true)}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 transition-colors px-3 py-2 rounded-lg hover:bg-slate-50"
            >
              <Filter className="w-4 h-4" />
              <span>Filter</span>
            </button>
            <button
              onClick={() => setProTipsOpen(true)}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-amber-600 transition-colors px-3 py-2 rounded-lg hover:bg-slate-50"
            >
              <Lightbulb className="w-4 h-4" />
              <span>Pro Tips</span>
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-4 py-4 sm:py-6 text-center border-t border-slate-100 bg-slate-50">
        <p className="text-xs text-slate-500 mb-2 sm:mb-3 max-w-md mx-auto leading-relaxed">
          Your photo and tryon output remain private and visible only to you. Store cannot access your images. All data is auto-deleted after the session expires.
        </p>
        <div className="flex items-center justify-center gap-2">
          <span className="text-xs text-slate-400">Powered by</span>
          <img
            src="/new-logo.png"
            alt="Nanokart.AI"
            className="h-5 w-auto"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline'; }}
          />
          <span className="text-sm font-semibold text-slate-600 hidden">Nanokart.AI</span>
        </div>
      </footer>

      {/* Modals */}
      <AnimatePresence>
        {cameraOpen && (
          <CameraCapture
            isOpen={cameraOpen}
            onCapture={async (file, previewUrl) => {
              await handleCameraCapture(file, previewUrl);
              setCameraOpen(false);
            }}
            onClose={() => setCameraOpen(false)}
            aspectRatio="3/4"
          />
        )}
        {filtersOpen && (
          <FiltersModal
            isOpen={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            filters={filters}
            onApply={setFilters}
            categories={categories}
          />
        )}
        {proTipsOpen && (
          <ProTipsModal
            isOpen={proTipsOpen}
            onClose={() => setProTipsOpen(false)}
          />
        )}
        {tryonLimitReached && (
          <TryonLimitModal
            isOpen={tryonLimitReached}
            onKeepExploring={() => setTryonLimitReached(false)}
            onEndSession={() => {
              setTryonLimitReached(false);
              handleEndSessionWithHistory('quota_exhausted');
            }}
          />
        )}
        {fullscreenGallery && (
          <FullscreenGallery
            images={fullscreenGallery.images}
            initialIndex={fullscreenGallery.initialIndex}
            onClose={() => setFullscreenGallery(null)}
          />
        )}
        {productDetailModal && (
          <ProductDetailModal
            product={productDetailModal}
            isOpen={!!productDetailModal}
            onClose={() => setProductDetailModal(null)}
            onTryOn={handleTryOn}
            onAddToCart={() => handleAddToCart(productDetailModal)}
            hasTryOnResult={!!tryOnCache[productDetailModal?.product_id || productDetailModal?.id]}
            isInCart={isInCart(productDetailModal?.product_id || productDetailModal?.id)}
            isLoading={tryOnLoading}
          />
        )}
      </AnimatePresence>

      {/* Cart drawer (slide-in from right) */}
      <CartDrawer
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        hardCart={hardCart}
        softCart={softCart}
        onRemoveFromCart={handleRemoveFromCart}
        onAddToCart={handleAddToCart}
        onCheckout={() => { handleCheckout(); setCartOpen(false); }}
        onEndSession={() => { setCartOpen(false); handleEndSessionWithHistory('user_ended'); }}
      />
    </div>
  );
};

export default CustomerTryOnStudio;
